import React from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const UploadErrorModal = ({
  open,
  onClose,
  onRetry,
  errorData
}) => {
  if (!errorData) return null;

  const { fileName, error, type = 'error', missingFiles, presentFiles } = errorData;

  const getErrorIcon = () => {
    switch (type) {
      case 'validation':
        return <WarningIcon sx={{ fontSize: 24, color: '#FF9800' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 24, color: 'warning.main' }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 24, color: 'info.main' }} />;
      default:
        return <ErrorIcon sx={{ fontSize: 24, color: 'error.main' }} />;
    }
  };

  const getErrorTitle = () => {
    switch (type) {
      case 'validation':
        return 'Missing Required Files';
      case 'warning':
        return 'Upload Warning';
      case 'info':
        return 'Upload Information';
      default:
        return 'Upload Failed';
    }
  };

  const getErrorSeverity = () => {
    switch (type) {
      case 'validation':
        return 'warning';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'error';
    }
  };

  const getRequiredFiles = () => [
    { ext: 'TRN', name: 'Transaction files', description: 'Contains transaction data' },
    { ext: 'ULT', name: 'Upload lot tax files', description: 'Contains lot tax information' },
    { ext: 'ULN', name: 'Upload loan files', description: 'Contains loan data' }
  ];

  const getMissingFiles = () => {
    if (!missingFiles) return [];
    return missingFiles.map(file => {
      const required = getRequiredFiles().find(r => r.ext === file);
      return required || { ext: file, name: `${file} files`, description: 'Required file type' };
    });
  };

  const getPresentFiles = () => {
    if (!presentFiles) return [];
    return presentFiles.map(file => {
      const required = getRequiredFiles().find(r => r.ext === file);
      return required || { ext: file, name: `${file} files`, description: 'Uploaded file type' };
    });
  };

  const getTroubleshootingTips = () => {
    const commonTips = [
      'Check if the file format is supported (ACC, RLC, RLD, RPS, SEC, TCF, TRN, ULN, ULT, UPN, UPT)',
      'Ensure the file is not corrupted or empty',
      'Verify the file encoding is UTF-8',
      'Check if the file size is under 10 MB',
      'Make sure the file has proper headers/structure'
    ];

    return commonTips;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="upload-error-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        
      }}
    >
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '450px',
        maxHeight: '70vh',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          backgroundColor: type === 'error' ? '#fff5f5' : type === 'warning' ? '#fffbf0' : '#f0f8ff',
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {getErrorIcon()}
            <Typography variant="h6" sx={{ 
              fontWeight: '600', 
              color: '#333',
              fontSize: '18px',
              margin: 0
            }}>
              {getErrorTitle()}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20, color: '#666' }} />
          </IconButton>
        </Box>

        {/* Error Details */}
        <Box sx={{ 
          padding: '0 20px 12px 20px',
          flex: 1,
          overflow: 'auto'
        }}>
          {type === 'validation' ? (
            <>
              <Alert severity={getErrorSeverity()} sx={{ mb: 2, py: 1 }}>
                <AlertTitle sx={{ fontSize: '14px', mb: 0.5 }}>Required Files Missing</AlertTitle>
                <Typography variant="body2" sx={{ fontSize: '13px' }}>
                  You must upload exactly 3 specific file types before proceeding. The following file type(s) is missing from your imported files. Would you like to proceed with the import without it?
                </Typography>
              </Alert>

              {/* Missing Files Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{
                  color: '#D32F2F',
                  fontSize: '14px',
                  fontWeight: '600',
                  mb: 1
                }}>
                  Missing file(s):
                </Typography>
                <List dense sx={{ pl: 0, py: 0 }}>
                  {getMissingFiles().map((file, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <ErrorOutlineIcon sx={{ color: '#D32F2F', fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{
                            color: '#D32F2F',
                            fontWeight: '600',
                            fontSize: '13px'
                          }}>
                            .{file.ext.toLowerCase()}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{
                            color: '#666',
                            fontSize: '11px'
                          }}>
                            {file.name} - {file.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Present Files Section */}
              {getPresentFiles().length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{
                    color: '#2E7D32',
                    fontSize: '14px',
                    fontWeight: '600',
                    mb: 1
                  }}>
                    Uploaded file(s):
                  </Typography>
                  <List dense sx={{ pl: 0, py: 0 }}>
                    {getPresentFiles().map((file, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckCircleOutlineIcon sx={{ color: '#2E7D32', fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography sx={{
                              color: '#2E7D32',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}>
                              .{file.ext.toLowerCase()}
                            </Typography>
                          }
                          secondary={
                            <Typography sx={{
                              color: '#666',
                              fontSize: '11px'
                            }}>
                              {file.name} - {file.description}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          ) : (
            <Alert severity={getErrorSeverity()} sx={{ mb: 2, py: 1 }}>
              <AlertTitle sx={{ fontSize: '14px', mb: 0.5 }}>Error Details</AlertTitle>
              <Typography variant="body2" sx={{ mt: 1, fontSize: '13px' }}>
                <strong>File:</strong> {fileName}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontSize: '13px' }}>
                <strong>Error:</strong> {error}
              </Typography>
            </Alert>
          )}
        </Box>

        {type !== 'validation' && (
          <>
            <Divider sx={{ mx: 3 }} />
            {/* Troubleshooting Section - Only for non-validation errors */}
            <Box sx={{ padding: '12px 20px', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <HelpOutlineIcon sx={{ fontSize: 16, color: '#1344EB' }} />
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: '600', 
                  color: '#333',
                  fontSize: '14px'
                }}>
                  Troubleshooting Tips
                </Typography>
              </Box>
              
              <List dense sx={{ py: 0 }}>
                {getTroubleshootingTips().slice(0, 3).map((tip, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.25, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 16, mt: 0.5 }}>
                      <Box sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: '#1344EB'
                      }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: 1.3 }}>
                          {tip}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}

        {/* Actions */}
        <Box sx={{
          padding: '12px 20px 16px 20px',
          backgroundColor: type === 'validation' ? '#fff' : '#fafafa',
          display: 'flex',
          gap: 1.5,
          justifyContent: 'flex-end',
          flexShrink: 0
        }}>
          <Button
            variant="outlined"
            onClick={onClose}
            size="small"
            sx={{
              color: type === 'validation' ? '#D32F2F' : '#666',
              borderColor: type === 'validation' ? '#D32F2F' : '#ddd',
              textTransform: 'none',
              fontWeight: '500',
              borderRadius: '6px',
              fontSize: '13px',
              px: 2,
              py: 0.5,
              '&:hover': {
                borderColor: type === 'validation' ? '#B71C1C' : '#bbb',
                backgroundColor: type === 'validation' ? 'rgba(211, 47, 47, 0.04)' : '#f5f5f5'
              }
            }}
          >
            {type === 'validation' ? 'Cancel' : 'Cancel'}
          </Button>
          
          {onRetry && (
            <Button
              variant="contained"
              startIcon={type === 'validation' ? null : <RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={onRetry}
              size="small"
              sx={{
                backgroundColor: type === 'validation' ? '#D32F2F' : '#1344EB',
                color: 'white',
                textTransform: 'none',
                fontWeight: '500',
                borderRadius: '6px',
                fontSize: '13px',
                px: 2,
                py: 0.5,
                '&:hover': {
                  backgroundColor: type === 'validation' ? '#B71C1C' : '#0D47A1'
                }
              }}
            >
              {type === 'validation' ? 'Proceed' : 'Try Again'}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadErrorModal; 